import type { AppBootstrap, ProviderPublicConfig, QueueItem, RemoteMessage, SongSummary } from '@pipi/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, websocketUrl } from '../lib/api';

type Toast = { id: number; message: string; tone: 'info' | 'success' | 'error' };

type AppStateValue = {
  data: AppBootstrap | null;
  loading: boolean;
  error: string | null;
  selectedSinger: string;
  toasts: Toast[];
  refresh: (quiet?: boolean) => Promise<void>;
  selectSinger: (name: string) => void;
  addToQueue: (song: SongSummary, singer?: string) => Promise<QueueItem | null>;
  removeFromQueue: (id: string) => Promise<void>;
  moveQueue: (id: string, direction: -1 | 1) => Promise<void>;
  setFavorite: (song: SongSummary, favorite: boolean) => Promise<void>;
  updateProvider: (id: string, update: { enabled?: boolean; name?: string; config?: Record<string, unknown> }) => Promise<void>;
  testProvider: (id: string) => Promise<{ ok: boolean; message: string }>;
  syncProvider: (id: string) => Promise<void>;
  notify: (message: string, tone?: Toast['tone']) => void;
};

const AppStateContext = createContext<AppStateValue | null>(null);

function updateSongs(data: AppBootstrap, songId: string, favorite: boolean): AppBootstrap {
  const patch = (songs: SongSummary[]) => songs.map((song) => (song.id === songId ? { ...song, favorite } : song));
  const all = [data.featured, data.recentlyAdded, data.favorites];
  const match = all.flat().find((song) => song.id === songId);
  return {
    ...data,
    featured: patch(data.featured),
    recentlyAdded: patch(data.recentlyAdded),
    favorites: favorite && match
      ? [...patch(data.favorites).filter((song) => song.id !== songId), { ...match, favorite: true }]
      : patch(data.favorites).filter((song) => song.id !== songId),
  };
}

export function AppStateProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<AppBootstrap | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSinger, setSelectedSinger] = useState('我们家');
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastId = useRef(0);

  const notify = useCallback((message: string, tone: Toast['tone'] = 'info') => {
    const id = ++toastId.current;
    setToasts((current) => [...current, { id, message, tone }]);
    window.setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 3200);
  }, []);

  const refresh = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const next = await api.bootstrap();
      setData(next);
      if (next.profiles.length && !next.profiles.some((profile) => profile.name === selectedSinger)) {
        setSelectedSinger(next.profiles[0]?.name ?? '我们家');
      }
      setError(null);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : '无法连接皮皮 K 歌服务');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [selectedSinger]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (window.location.pathname === '/remote') return;
    let socket: WebSocket | undefined;
    let retry: number | undefined;
    const connect = () => {
      socket = new WebSocket(websocketUrl());
      socket.addEventListener('open', () => socket?.send(JSON.stringify({ type: 'hello', payload: { role: 'tv' }, sender: 'tv' } satisfies RemoteMessage)));
      socket.addEventListener('message', (event) => {
        try {
          const message = JSON.parse(String(event.data)) as RemoteMessage;
          if (message.type === 'key') window.dispatchEvent(new CustomEvent('pipi:remote-key', { detail: message.payload.key }));
          if (message.type === 'player:command') window.dispatchEvent(new CustomEvent('pipi:player-command', { detail: message.payload.command }));
          if (message.type === 'state' && (message.payload.queue || message.payload.catalogChanged || message.payload.favoriteChanged)) void refresh(true);
        } catch {
          // Ignore malformed third-party remote packets.
        }
      });
      socket.addEventListener('close', () => {
        retry = window.setTimeout(connect, 2500);
      });
    };
    connect();
    return () => {
      if (retry) window.clearTimeout(retry);
      socket?.close();
    };
  }, [refresh]);

  const addToQueue = useCallback(async (song: SongSummary, singer = selectedSinger) => {
    try {
      const item = await api.addQueue(song.id, singer);
      setData((current) => (current ? { ...current, queue: [...current.queue, item] } : current));
      notify(`《${song.title}》已加入点歌台`, 'success');
      return item;
    } catch (requestError) {
      notify(requestError instanceof Error ? requestError.message : '点歌失败', 'error');
      return null;
    }
  }, [notify, selectedSinger]);

  const removeFromQueue = useCallback(async (id: string) => {
    await api.removeQueue(id);
    setData((current) => (current ? { ...current, queue: current.queue.filter((item) => item.id !== id) } : current));
    notify('已从点歌台移除');
  }, [notify]);

  const moveQueue = useCallback(async (id: string, direction: -1 | 1) => {
    const queue = await api.moveQueue(id, direction);
    setData((current) => (current ? { ...current, queue } : current));
  }, []);

  const setFavorite = useCallback(async (song: SongSummary, favorite: boolean) => {
    await api.favorite(song.id, favorite);
    setData((current) => (current ? updateSongs(current, song.id, favorite) : current));
    notify(favorite ? '已收藏' : '已取消收藏', favorite ? 'success' : 'info');
  }, [notify]);

  const updateProvider = useCallback(async (id: string, update: { enabled?: boolean; name?: string; config?: Record<string, unknown> }) => {
    const provider = await api.updateProvider(id, update);
    setData((current) => current ? { ...current, providers: current.providers.map((item) => item.id === id ? provider : item) } : current);
    notify('曲库设置已保存', 'success');
  }, [notify]);

  const testProvider = useCallback(async (id: string) => {
    const result = await api.testProvider(id);
    notify(result.message, result.ok ? 'success' : 'error');
    void refresh(true);
    return result;
  }, [notify, refresh]);

  const syncProvider = useCallback(async (id: string) => {
    notify('正在同步曲库…');
    const result = await api.syncProvider(id);
    notify(`同步完成，共 ${result.count.toLocaleString()} 首`, 'success');
    await refresh(true);
  }, [notify, refresh]);

  const value = useMemo<AppStateValue>(() => ({
    data,
    loading,
    error,
    selectedSinger,
    toasts,
    refresh,
    selectSinger: setSelectedSinger,
    addToQueue,
    removeFromQueue,
    moveQueue,
    setFavorite,
    updateProvider,
    testProvider,
    syncProvider,
    notify,
  }), [data, loading, error, selectedSinger, toasts, refresh, addToQueue, removeFromQueue, moveQueue, setFavorite, updateProvider, testProvider, syncProvider, notify]);

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}

export function useAppState() {
  const value = useContext(AppStateContext);
  if (!value) throw new Error('useAppState must be used inside AppStateProvider');
  return value;
}
